import {
  Component,
  startTransition,
  useEffect,
  useRef,
  useState,
  type ErrorInfo,
  type ReactNode,
} from 'react';
import { useInView, type UseInViewOptions } from 'framer-motion';
import Lottie, { type LottieProps } from 'react-lottie-player';

interface LottieErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface LottieErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export class LottieErrorBoundary extends Component<
  LottieErrorBoundaryProps,
  LottieErrorBoundaryState
> {
  constructor(props: LottieErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): LottieErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Lottie Player Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

export default function LottiePlayer({
  fallback,
  ...props
}: LottieProps & { fallback: ReactNode }) {
  const ref = useRef(null);

  return (
    <LottieErrorBoundary fallback={fallback}>
      <Lottie ref={ref} {...props} />
    </LottieErrorBoundary>
  );
}

export function InViewLottiePlayer({
  fallback,
  inViewOptions = { amount: 0.3 },
  inViewDelay,
  ...props
}: LottieProps & {
  fallback: ReactNode;
  inViewOptions?: UseInViewOptions;
  inViewDelay?: number;
}) {
  const ref = useRef(null);
  const containerRef = useRef(null);
  const [shouldPlay, setShouldPlay] = useState(false);

  const isInView = useInView(containerRef, inViewOptions);

  useEffect(() => {
    if (!isInView) {
      startTransition(() => setShouldPlay(false));
      return;
    }

    const id = window.setTimeout(() => {
      startTransition(() => {
        setShouldPlay(true);
      });
    }, inViewDelay ?? 0);
    return () => window.clearTimeout(id);
  }, [isInView, inViewDelay]);

  return (
    <div ref={containerRef}>
      <LottieErrorBoundary fallback={fallback}>
        <Lottie ref={ref} {...props} play={shouldPlay} />
      </LottieErrorBoundary>
    </div>
  );
}
